import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import type { Icon } from "@tabler/icons-react";
import {
  IconSearch,
  IconChartDots,
  IconWand,
  IconMessage2,
  IconPointer,
  IconLayoutKanban,
  IconArrowRight,
  IconCheck,
  IconChevronDown,
  IconBolt,
} from "@tabler/icons-react";
import { CREDIT_PACKS } from "@jobwarp/shared";
import { authClient } from "../auth/client.js";
import { HeroParticles } from "../components/landing/HeroParticles.js";
import { DemoShowcase } from "../components/landing/DemoShowcase.js";
import { DemoVideoModal } from "../components/landing/DemoVideoModal.js";
import { Seo } from "../components/Seo.js";

// The single account-first CTA used across the landing. Signed-out visitors go
// to the sign-up screen (where they pick Google or email/password); signed-in
// visitors get a shortcut into the app. The extension is no longer a landing
// CTA — it is installed during onboarding, after sign-up.
function SignUpButton({
  signedIn,
  className = "btn-primary hover:shadow-focus",
  label = "Sign up free",
}: {
  signedIn: boolean;
  className?: string;
  label?: string;
}) {
  return (
    <Link to={signedIn ? "/app" : "/signin?mode=signup"} className={className}>
      {signedIn ? "Open JobWarp" : label}
    </Link>
  );
}

// Slim mid-page sign-up band, repeated as the visitor scrolls.
function SignUpBand({ heading, signedIn }: { heading: string; signedIn: boolean }) {
  return (
    <section className="border-b border-default">
      <Container className="py-12 md:py-14">
        <div className="bg-panel border border-default rounded-xl p-6 md:p-8 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-20 font-medium leading-heading tracking-heading text-primary max-w-md">
              {heading}
            </h3>
            <p className="mt-1.5 text-13 leading-prose text-secondary">
              Free to start. 3 Warps free, no card.
            </p>
          </div>
          <div className="flex flex-col items-start gap-2 sm:items-end flex-none">
            <SignUpButton signedIn={signedIn} />
            <p className="text-11 text-tertiary sm:text-right">
              Works in Chrome, Edge &amp; Firefox.
            </p>
          </div>
        </div>
      </Container>
    </section>
  );
}

export function Landing() {
  const [signedIn, setSignedIn] = useState(false);
  const [showDemo, setShowDemo] = useState(false);

  useEffect(() => {
    void authClient
      .session()
      .then((s) => setSignedIn(!!s?.user))
      // Network failure just means we render the signed-out landing.
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-page text-primary">
      <Seo
        title="JobWarp. Qualify and apply to jobs faster."
        description="JobWarp scores any job posting against your CV, drafts a tailored application, and preps you for the interview, right in your browser."
        path="/"
      />
      <SiteHeader signedIn={signedIn} />
      <main>
        <Hero signedIn={signedIn} onWatchDemo={() => setShowDemo(true)} />
        <ProblemStatement />
        <HowItWorks />
        <DemoShowcase />
        <SignUpBand signedIn={signedIn} heading="Stop guessing which roles deserve your time." />
        <KeyFeatures />
        <DeeperShowcase />
        <Pricing signedIn={signedIn} />
        <SignUpBand signedIn={signedIn} heading="Your first three Warps are on us." />
        <FAQ />
        <FinalCTA signedIn={signedIn} />
      </main>
      <SiteFooter />

      {/* Floating closable demo video modal (opened from hero "Watch the demo") */}
      <DemoVideoModal
        open={showDemo}
        onClose={() => setShowDemo(false)}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   Layout primitives
   ────────────────────────────────────────────────────────────── */

function Container({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`mx-auto w-full max-w-5xl px-6 ${className}`}>{children}</div>
  );
}

function SectionEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-11 font-mono uppercase tracking-mono-caps text-tertiary">
      {children}
    </p>
  );
}

function SectionHeading({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <h2
      className={`text-28 font-medium leading-display tracking-heading text-primary ${className}`}
    >
      {children}
    </h2>
  );
}

/* ──────────────────────────────────────────────────────────────
   Header
   ────────────────────────────────────────────────────────────── */

function SiteHeader({ signedIn }: { signedIn: boolean }) {
  return (
    <header className="border-b border-default bg-page/80 backdrop-blur sticky top-0 z-20">
      <Container className="flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <DustMark className="w-5 h-5 text-accent-lime" aria-hidden="true" />
          <Wordmark size={20} />
        </Link>
        <nav className="flex items-center gap-3 sm:gap-5">
          <a
            href="#how-it-works"
            className="hidden sm:inline text-13 text-secondary hover:text-primary transition-colors"
          >
            How it works
          </a>
          <a
            href="#pricing"
            className="hidden sm:inline text-13 text-secondary hover:text-primary transition-colors"
          >
            Pricing
          </a>
          <a
            href="#faq"
            className="hidden sm:inline text-13 text-secondary hover:text-primary transition-colors"
          >
            FAQ
          </a>
          {signedIn ? (
            <Link to="/app" className="btn-primary text-13 hover:shadow-focus">
              Dashboard
            </Link>
          ) : (
            <>
              <Link
                to="/signin"
                className="hidden sm:inline text-13 text-secondary hover:text-primary"
              >
                Log in
              </Link>
              <Link
                to="/signin?mode=signup"
                className="btn-primary text-13 hover:shadow-focus"
              >
                Sign up free
              </Link>
            </>
          )}
        </nav>
      </Container>
    </header>
  );
}

function Wordmark({ size = 20 }: { size?: 15 | 20 | 28 }) {
  return (
    <span
      style={{ fontSize: size }}
      className="font-serif italic font-normal leading-tight text-primary tracking-heading"
    >
      JobWarp
    </span>
  );
}

/* ──────────────────────────────────────────────────────────────
   1. Hero
   ────────────────────────────────────────────────────────────── */

function Hero({
  signedIn,
  onWatchDemo,
}: {
  signedIn: boolean;
  onWatchDemo: () => void;
}) {
  return (
    <section className="relative overflow-hidden border-b border-default">
      {/* Ambient dust field — decoration only, faded out toward the bottom. */}
      <HeroParticles />
      <Container className="relative py-16 md:py-24">
        <div className="grid lg:grid-cols-[1fr_minmax(0,520px)] gap-12 lg:gap-16 items-start">
          {/* Text + CTA column */}
          <div>
            <div className="jw-rise" style={{ animationDelay: "0s" }}>
              <SectionEyebrow>Chrome, Edge & Firefox extension</SectionEyebrow>
            </div>
            {/* The h1 and hero figure stay un-animated on purpose: they are
                the page's LCP candidates, and an opacity-from-0 entrance
                disqualifies them (Lighthouse then scores LCP on the tiny
                header wordmark instead). Only supporting elements rise. */}
            <h1 className="mt-4 text-28 font-medium leading-display tracking-heading text-primary">
              Get more interviews. Spend less time on each application.
            </h1>
            <p
              className="jw-rise mt-5 text-15 leading-prose text-secondary max-w-xl"
              style={{ animationDelay: ".1s" }}
            >
              A browser extension that works on any job posting on the web. It
              scores how well you fit, drafts a cover letter and screening
              answers from your real experience, and preps you for the
              interview. Fewer applications, sharper ones.
            </p>

            <div
              className="jw-rise mt-8 flex flex-wrap items-center gap-3"
              style={{ animationDelay: ".24s" }}
            >
              <SignUpButton signedIn={signedIn} />
              <button
                type="button"
                onClick={onWatchDemo}
                className="btn-secondary hover:bg-elevated"
              >
                Watch the demo
                <IconArrowRight size={14} />
              </button>
            </div>

            <p
              className="jw-rise mt-3 text-11 text-tertiary"
              style={{ animationDelay: ".3s" }}
            >
              Free to start. 3 Warps free, no card.
            </p>

            <p
              className="jw-rise mt-4 text-11 font-mono uppercase tracking-mono-caps text-tertiary"
              style={{ animationDelay: ".36s" }}
            >
              Works on any job posting, in Chrome, Edge &amp; Firefox
            </p>
          </div>

          {/* Product screenshot column — JobWarp injected on a real-style JD,
              captured from the canonical mockup at apps/extension/mockups/. */}
          <figure>
            <div className="relative bg-panel border border-default rounded-xl overflow-hidden shadow-panel">
              {/* hairline lime edge along the top of the shot */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-px"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, rgba(199,247,81,.5) 30%, rgba(199,247,81,.5) 70%, transparent)",
                }}
              />
              <img
                src="/hero-panel.webp"
                alt="The JobWarp extension panel injected on a LinkedIn job posting, showing an 8 out of 10 strong fit score and the reasoning."
                width={760}
                height={573}
                className="block w-full h-auto"
                loading="eager"
                // React 18 has no fetchPriority prop; the lowercase form
                // passes through to the DOM (and the SSG HTML) as-is.
                {...{ fetchpriority: "high" }}
              />
            </div>
            <figcaption className="mt-3 text-11 font-mono uppercase tracking-mono-caps text-tertiary text-right">
              Scores the role. Drafts the application.
            </figcaption>
          </figure>
        </div>
      </Container>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────
   2. Problem statement
   ────────────────────────────────────────────────────────────── */

function ProblemStatement() {
  return (
    <section className="border-b border-default">
      <Container className="py-16 md:py-24">
        <div className="grid lg:grid-cols-[280px_1fr] gap-12 lg:gap-16">
          <div>
            <SectionEyebrow>Why the search drags on</SectionEyebrow>
            <p className="mt-4 text-20 font-medium leading-heading tracking-heading text-primary">
              Most of the hours you spend applying are wasted.
            </p>
          </div>
          <div className="max-w-2xl space-y-5">
            <p className="text-15 leading-prose text-secondary">
              Send generic applications and they go to the trash, so the
              interviews never come. Do each one properly and it takes an hour
              you do not have. Either way the search drags on for months.
            </p>
            <p className="text-15 leading-prose text-secondary">
              JobWarp fixes both. It scores how well you match each role, so you
              spend your time on the ones worth it, and it handles the research,
              cover letter, and interview prep, so a strong application takes
              seconds instead of an hour.
            </p>
          </div>
        </div>
        {/* full-width proof band — breaks out of the narrow text column so the
            cards have room to breathe */}
        <ProblemProofCards />
      </Container>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────
   2b. Problem-section proof cards
   Three illustrated cards under the problem copy — fit score, company
   research, application ready — that show what JobWarp resolves. They
   reveal on scroll-in (staggered), sweep a diagonal sheen, and tilt in
   3D toward the cursor. All motion is disabled under reduced-motion.
   ────────────────────────────────────────────────────────────── */

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduce(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return reduce;
}

// Diagonal highlight sweep via the Web Animations API, so a hover can
// replay it on demand without fighting CSS animation state.
function runSheen(el: HTMLElement) {
  el.animate(
    [
      { transform: "translateX(-130%)", opacity: 0, offset: 0 },
      { opacity: 1, offset: 0.1 },
      { opacity: 1, offset: 0.9 },
      { transform: "translateX(130%)", opacity: 0, offset: 1 },
    ],
    { duration: 650, easing: "cubic-bezier(.4,0,.2,1)" },
  );
}

function ProofCard({
  index,
  accent,
  revealed,
  reduce,
  children,
}: {
  index: number;
  accent: string;
  revealed: boolean;
  reduce: boolean;
  children: React.ReactNode;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const sheenRef = useRef<HTMLSpanElement>(null);

  // Entrance sheen, staggered per card, once the row scrolls into view.
  useEffect(() => {
    if (!revealed || reduce || !sheenRef.current) return;
    const t = window.setTimeout(() => {
      if (sheenRef.current) runSheen(sheenRef.current);
    }, 450 + index * 150);
    return () => window.clearTimeout(t);
  }, [revealed, reduce, index]);

  function handleMove(e: React.PointerEvent<HTMLDivElement>) {
    if (reduce || !rootRef.current) return;
    const el = rootRef.current;
    const r = el.getBoundingClientRect();
    const dx = (e.clientX - r.left) / r.width - 0.5;
    const dy = (e.clientY - r.top) / r.height - 0.5;
    el.style.transitionDuration = "80ms";
    // Sign chosen so the corner under the pointer dips away from the viewer.
    el.style.transform = `perspective(900px) rotateX(${(-dy * 14).toFixed(
      2,
    )}deg) rotateY(${(dx * 14).toFixed(2)}deg)`;
  }
  function handleLeave() {
    if (!rootRef.current) return;
    const el = rootRef.current;
    el.style.transitionDuration = "400ms";
    el.style.transform = "perspective(900px) rotateX(0deg) rotateY(0deg)";
  }
  function handleEnter() {
    if (!reduce && sheenRef.current) runSheen(sheenRef.current);
  }

  return (
    <div
      ref={rootRef}
      onPointerMove={handleMove}
      onPointerLeave={handleLeave}
      onPointerEnter={handleEnter}
      className="group relative isolate flex flex-col overflow-hidden rounded-xl border border-default bg-panel p-8 hover:border-strong"
      style={{
        opacity: revealed || reduce ? 1 : 0,
        transform: "perspective(900px)",
        transformStyle: "preserve-3d",
        transition:
          "opacity .6s cubic-bezier(.2,.7,.3,1), transform .4s cubic-bezier(.2,.7,.3,1), border-color .3s",
        transitionDelay: revealed && !reduce ? `${index * 0.1}s` : "0s",
      }}
    >
      {/* faint illustrative wash, top-left */}
      <span
        aria-hidden
        className="pointer-events-none absolute -left-12 -top-16 -z-10 h-48 w-48 rounded-full opacity-10 blur-[60px]"
        style={{ background: accent }}
      />
      {/* hairline accent along the top edge */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-px"
        style={{ background: `linear-gradient(90deg, ${accent}, transparent 60%)` }}
      />
      {/* diagonal sheen */}
      <span
        ref={sheenRef}
        aria-hidden
        className="pointer-events-none absolute -inset-0.5 z-[2] rounded-xl opacity-0"
        style={{
          background:
            "linear-gradient(105deg,transparent 35%,rgba(255,255,255,.10) 48%,rgba(255,255,255,.18) 50%,rgba(255,255,255,.10) 52%,transparent 65%)",
        }}
      />
      {children}
    </div>
  );
}

function CardLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-6 font-mono text-11 uppercase tracking-mono-caps text-secondary">
      {children}
    </p>
  );
}

function ScoreRing({
  value,
  revealed,
  reduce,
}: {
  value: number;
  revealed: boolean;
  reduce: boolean;
}) {
  const C = 2 * Math.PI * 32; // ring circumference
  const target = C * (1 - value / 10);
  return (
    <div className="relative h-[72px] w-[72px] flex-none">
      <svg width="72" height="72" viewBox="0 0 72 72" className="-rotate-90">
        <circle
          cx="36"
          cy="36"
          r="32"
          fill="none"
          strokeWidth="6"
          className="stroke-elevated"
        />
        <circle
          cx="36"
          cy="36"
          r="32"
          fill="none"
          strokeWidth="6"
          strokeLinecap="round"
          className="stroke-accent-lime"
          style={{
            strokeDasharray: C,
            strokeDashoffset: revealed || reduce ? target : C,
            transition: reduce ? "none" : "stroke-dashoffset 1s ease .35s",
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <b className="text-[24px] font-semibold tracking-[-.02em] text-primary">
          {value}
        </b>
        <span className="mt-0.5 font-mono text-[10px] text-tertiary">/10</span>
      </div>
    </div>
  );
}

function ResearchRow({
  label,
  tone,
  value,
}: {
  label: string;
  tone: "neutral" | "good" | "warn" | "bad";
  value: string;
}) {
  const tones: Record<typeof tone, string> = {
    neutral: "bg-elevated text-primary border-default",
    good: "bg-accent-lime-06 text-accent-lime border-accent-lime-30",
    warn: "bg-warning-amber-06 text-warning-amber border-warning-amber-30",
    bad: "bg-destructive-red-08 text-destructive-red border-destructive-red/30",
  };
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-13 text-secondary">{label}</span>
      <span
        className={`whitespace-nowrap rounded-md border px-2 py-0.5 font-mono text-11 font-medium ${tones[tone]}`}
      >
        {value}
      </span>
    </div>
  );
}

function CheckItem({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 text-13 text-primary">
      <span className="grid h-[18px] w-[18px] flex-none place-items-center rounded-md border border-accent-lime-30 bg-accent-lime-06">
        <IconCheck size={11} className="text-accent-lime" stroke={2.5} />
      </span>
      {children}
    </div>
  );
}

function ProblemProofCards() {
  const rowRef = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);
  const reduce = usePrefersReducedMotion();

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) {
      setRevealed(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setRevealed(true);
            obs.disconnect();
          }
        });
      },
      // Reveal as the row enters from the bottom: the small negative bottom
      // margin trims the lowest ~12% of the viewport, so the cards animate in
      // when their top edge is ~88% down the screen — just as they appear,
      // not after the user has already scrolled past them.
      { threshold: 0, rootMargin: "0px 0px -12% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={rowRef}
      className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-3 md:mt-16"
      style={{ perspective: "900px" }}
    >
      <ProofCard index={0} accent="#C7F751" revealed={revealed} reduce={reduce}>
        <CardLabel>Fit score</CardLabel>
        <div className="flex flex-1 flex-col justify-center gap-5">
          <div className="flex items-center gap-4">
            <ScoreRing value={8} revealed={revealed} reduce={reduce} />
            <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-accent-lime-30 bg-accent-lime-06 px-3 py-1 text-11 font-medium text-accent-lime">
              <span className="h-1.5 w-1.5 flex-none rounded-full bg-accent-lime" />
              Strong fit
            </span>
          </div>
          <p className="text-13 leading-prose text-secondary">
            Scores your CV against what the JD asks for. Skip the ones that don't.
          </p>
        </div>
      </ProofCard>

      <ProofCard index={1} accent="#E0A95C" revealed={revealed} reduce={reduce}>
        <CardLabel>Company research</CardLabel>
        <div className="flex flex-1 flex-col justify-center gap-4">
          <ResearchRow label="Glassdoor" tone="good" value="3.9 ★" />
          <ResearchRow label="CEO approval" tone="warn" value="71%" />
          <ResearchRow label="Layoffs" tone="bad" value="Jan 2026" />
          <ResearchRow label="Funding" tone="neutral" value="Series B" />
        </div>
      </ProofCard>

      <ProofCard index={2} accent="#C7F751" revealed={revealed} reduce={reduce}>
        <CardLabel>Application ready</CardLabel>
        <div className="flex flex-1 flex-col justify-center gap-4">
          <CheckItem>Cover letter drafted</CheckItem>
          <CheckItem>Screening answers</CheckItem>
          <CheckItem>Interview prep</CheckItem>
        </div>
        <span className="mt-6 inline-flex w-fit items-center gap-2 rounded-full border border-accent-lime-30 bg-accent-lime-06 px-3 py-1 font-mono text-11 uppercase tracking-mono-caps text-accent-lime">
          <IconBolt size={11} className="fill-accent-lime text-accent-lime" />
          Ready in seconds
        </span>
      </ProofCard>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   3. How it works
   ────────────────────────────────────────────────────────────── */

function HowItWorks() {
  const steps = [
    {
      label: "Step 01",
      title: "Set it up once.",
      body: "Add the extension and upload your real CV, the one you actually send. Targeting two kinds of role? Add a second CV. It takes about two minutes, then you never touch setup again.",
      image: {
        src: "/step-setup.webp",
        alt: "A browser with the JobWarp extension open, linked to an uploaded CV showing experience and skills.",
      },
    },
    {
      label: "Step 02",
      title: "See your fit in one click.",
      body: "On LinkedIn, Indeed, Greenhouse, Workday, or any career page, JobWarp reads the full JD and researches the company, then scores how well you match, with the reasoning behind it. Scoring every role is free.",
      image: {
        src: "/step-fit.webp",
        alt: "A CV and a job listing side by side with a green scan line and a 94% fit score above them.",
      },
    },
    {
      label: "Step 03",
      title: "Skip the weak ones. Warp the rest.",
      body: "Low score? Move on without losing the evening. Strong score? Warp it, and JobWarp drafts the cover letter and screening answers in seconds, so the careful application no longer costs you an hour. When the interview lands, it preps you for that too.",
      image: {
        src: "/step-skip.webp",
        alt: "A stack of raw job applications pulled through a glowing Warp Engine into polished, tailored ones.",
      },
    },
  ];

  return (
    <section id="how-it-works" className="scroll-mt-16 border-b border-default">
      <Container className="py-16 md:py-24">
        <SectionEyebrow>Three steps</SectionEyebrow>
        <SectionHeading className="mt-4 max-w-2xl">
          From a job posting to a tailored application and interview prep, in
          under a minute.
        </SectionHeading>

        <div className="mt-12 grid md:grid-cols-3 gap-4">
          {steps.map((step) => (
            <article
              key={step.label}
              className="group bg-panel border border-default rounded-xl overflow-hidden flex flex-col transition-colors duration-300 hover:border-strong"
            >
              <div className="overflow-hidden">
                <img
                  src={step.image.src}
                  alt={step.image.alt}
                  width={1200}
                  height={746}
                  className="block w-full h-auto transition-transform duration-500 ease-out group-hover:scale-[1.025] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                  loading="lazy"
                />
              </div>
              <div className="p-5 flex flex-col">
                <p className="text-11 font-mono uppercase tracking-mono-caps text-tertiary">
                  {step.label}
                </p>
                <h3 className="mt-2 text-15 font-medium leading-heading tracking-heading text-primary">
                  {step.title}
                </h3>
                <p className="mt-2 text-13 leading-prose text-secondary">
                  {step.body}
                </p>
              </div>
            </article>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────
   4. Key features
   ────────────────────────────────────────────────────────────── */

function KeyFeatures() {
  const tiles = [
    {
      Icon: IconSearch,
      title: "Reads the JD and researches the company",
      body: "JobWarp does not stop at the posting. It pulls together what the company does, recent moves, and how the team is shaped, then uses that to ground everything else it writes.",
    },
    {
      Icon: IconChartDots,
      title: "Scores the fit honestly",
      body: "A score from one to ten, with the reasoning underneath. Strong matches called out. Gaps called out plainly. We tune the score to be a little pessimistic rather than flatter you.",
    },
    {
      Icon: IconWand,
      title: "Drafts the full application",
      body: "Cover letter and screening answers, generated from your real experience, not a template library. Edit any of them in place.",
    },
    {
      Icon: IconMessage2,
      title: "Prepares you for the interview",
      body: "When the interview is scheduled, JobWarp generates a prep pack. Likely questions for this role and company. Notes on the team. A draft answer for each, based on your CV.",
    },
    {
      Icon: IconPointer,
      title: "Everything on the posting where you found it",
      body: "The score, the materials, and the prep all happen right on the job page. One click, no detour to another app.",
    },
    {
      Icon: IconLayoutKanban,
      title: "A funnel that stays accurate on its own",
      body: "Your kanban reflects where every role really stands, from Saved to Offer, without you maintaining a spreadsheet on the side.",
    },
  ];

  return (
    <section className="border-b border-default">
      <Container className="py-16 md:py-24">
        <SectionEyebrow>What you actually get</SectionEyebrow>
        <SectionHeading className="mt-4 max-w-2xl">
          Everything between finding the role and walking into the interview.
        </SectionHeading>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 border border-default rounded-xl overflow-hidden">
          {tiles.map((t, i) => (
            <FeatureCell key={t.title} Icon={t.Icon} title={t.title} body={t.body} index={i} />
          ))}
        </div>
      </Container>
    </section>
  );
}

function FeatureCell({
  Icon,
  title,
  body,
  index,
}: {
  Icon: Icon;
  title: string;
  body: string;
  index: number;
}) {
  // Manual hairline grid via border classes — keeps every line at 1px without doubling.
  // 3-column on lg: top row (0,1,2) no top border; first column no left border.
  // 2-column on sm: top row (0,1) no top border; first column no left border.
  return (
    <div
      className={[
        "group p-6 bg-panel transition-colors duration-300 hover:bg-elevated",
        "border-default",
        // top borders (mobile single-col): row 0 no top
        index !== 0 ? "border-t" : "",
        // sm: 2 cols — left border on odd; row 0,1 no top
        "sm:border-t sm:border-l",
        index < 2 ? "sm:border-t-0" : "",
        index % 2 === 0 ? "sm:border-l-0" : "",
        // lg: 3 cols — left border on cols 1,2; row 0,1,2 no top
        "lg:border-l",
        index < 3 ? "lg:border-t-0" : "lg:border-t",
        index % 3 === 0 ? "lg:border-l-0" : "lg:border-l",
      ].join(" ")}
    >
      <Icon
        size={20}
        stroke={1.5}
        className="text-primary transition-colors duration-300 group-hover:text-accent-lime"
      />
      <h3 className="mt-4 text-15 font-medium leading-heading tracking-heading text-primary">
        {title}
      </h3>
      <p className="mt-2 text-13 leading-prose text-secondary">{body}</p>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   5. Deeper showcase
   ────────────────────────────────────────────────────────────── */

function DeeperShowcase() {
  const blocks = [
    {
      eyebrow: "Triage",
      heading: "Know if a role is worth applying to before you spend an hour on it.",
      body: "Every JobWarp score comes with the reasoning. Three to five concrete points that compare what the JD asks for to what your CV shows. If the role wants ten years of Kubernetes and you have two, the score reflects that. We would rather show you a five than sell you a seven.",
      placeholder: "Extension popup. Score donut + why-this-score bullets.",
      image: {
        src: "/showcase-triage.webp",
        alt: "JobWarp panel showing an 8 of 10 score, a Strong fit verdict, and three why-this-score bullets.",
        width: 808,
        height: 1203,
      },
      reverse: false,
      accent: true,
    },
    {
      eyebrow: "Tailored materials",
      heading: "A cover letter, screening answers, a fit score, and interview prep for this exact role.",
      body: "The AI reads your CV, the JD, and what it found about the company, then produces a first draft of each piece. The cover letter references real details from the posting. The screening answers are short and direct. The fit score shows how well you match before you invest the time. Open any of them, edit, send.",
      placeholder: "Job detail page. Generated materials in tabs.",
      slot: <MaterialsMockup />,
      reverse: true,
    },
    {
      eyebrow: "Funnel tracking",
      heading: "Stop losing track of where every application stands.",
      body: "A real search spreads across dozens of roles, and the spreadsheet falls behind by week two. JobWarp notices when you apply and logs it for you, then keeps the columns current as replies and invites come in. The funnel reflects reality without you maintaining it.",
      placeholder: "Dashboard kanban. Saved, Applied, Interviewing, Offer.",
      slot: <FunnelMockup />,
      reverse: false,
    },
  ];

  return (
    <section className="border-b border-default">
      <Container className="py-16 md:py-24 space-y-16 md:space-y-24">
        {blocks.map((b) => (
          <ShowcaseBlock key={b.heading} {...b} />
        ))}
      </Container>
    </section>
  );
}

type ShowcaseImage = {
  src: string;
  alt: string;
  width: number;
  height: number;
  /** Tailwind max-width class for the image (default: max-w-xs). */
  maxW?: string;
};

function ShowcaseBlock({
  eyebrow,
  heading,
  body,
  placeholder,
  image,
  slot,
  reverse,
  accent,
}: {
  eyebrow: string;
  heading: string;
  body: string;
  placeholder: string;
  image?: ShowcaseImage;
  slot?: React.ReactNode;
  reverse?: boolean;
  accent?: boolean;
}) {
  return (
    <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
      <div className={reverse ? "lg:order-2" : ""}>
        <SectionEyebrow>{eyebrow}</SectionEyebrow>
        <h3 className="mt-3 text-20 md:text-28 font-medium leading-display tracking-heading text-primary">
          {heading}
        </h3>
        <p className="mt-5 text-15 leading-prose text-secondary max-w-xl">
          {body}
        </p>
        {accent && (
          <p className="mt-6 inline-flex items-center gap-2 text-13 text-accent-lime">
            <IconCheck size={14} stroke={2} />
            Pass faster. Apply with conviction.
          </p>
        )}
      </div>
      <div className={reverse ? "lg:order-1" : ""}>
        {slot ? (
          slot
        ) : image ? (
          <img
            src={image.src}
            alt={image.alt}
            width={image.width}
            height={image.height}
            className={`block w-full h-auto mx-auto ${image.maxW ?? "max-w-xs"}`}
            loading="lazy"
          />
        ) : (
          <div
            className="w-full bg-elevated border border-strong rounded-xl flex items-center justify-center"
            style={{ aspectRatio: "4 / 3" }}
          >
            {/* SCREENSHOT: {placeholder} */}
            <span className="text-11 font-mono uppercase tracking-mono-caps text-tertiary px-6 text-center">
              {placeholder}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function FunnelMockup() {
  const columns = [
    {
      label: "Saved",
      count: "12",
      company: "Notion",
      role: "Staff Product Manager",
      status: "7/10 fit",
      warped: false,
    },
    {
      label: "Applied",
      count: "4",
      company: "Vercel",
      role: "Engineering Manager",
      status: "Just applied",
      warped: true,
    },
    {
      label: "Interviewing",
      count: "2",
      company: "Linear",
      role: "Senior Backend Eng",
      status: "Onsite Wed",
      warped: false,
    },
  ];

  return (
    <div className="mx-auto w-full max-w-xl">
      <div className="grid grid-cols-3 gap-3">
        {columns.map((col) => (
          <div key={col.label}>
            <div className="flex items-baseline justify-between pb-2 mb-3 border-b border-default">
              <span className="text-13 font-medium text-primary">
                {col.label}
              </span>
              <span className="text-11 font-mono uppercase tracking-mono-caps text-tertiary">
                {col.count}
              </span>
            </div>
            <article
              className={`bg-panel border rounded-lg p-3 ${
                col.warped ? "border-accent-lime" : "border-default"
              }`}
            >
              <p className="text-11 font-mono uppercase tracking-mono-caps text-tertiary">
                {col.company}
              </p>
              <p className="mt-1 text-13 font-medium leading-heading text-primary">
                {col.role}
              </p>
              <p
                className={`mt-3 text-11 font-mono uppercase tracking-mono-caps ${
                  col.warped ? "text-accent-lime" : "text-tertiary"
                }`}
              >
                {col.status}
              </p>
            </article>
          </div>
        ))}
      </div>
    </div>
  );
}

function MaterialsMockup() {
  const cards = [
    {
      title: "Cover letter",
      meta: "Drafted · 412 words",
      snippet:
        "Qida's recent €37M raise signals a pivotal moment for tech-enabled elderly care in Spain. Your ambition to serve 100,000 seniors by 2027 maps directly to the scalable systems I have shipped before.",
    },
    {
      title: "Screening answers",
      meta: "3 questions · short and direct",
      snippet:
        'Why this role. This role pairs general management with hands-on AI product development, which is exactly where I have been shipping for the past two years.',
    },
  ];

  return (
    <div className="mx-auto w-full max-w-md space-y-3">
      {cards.map((c) => (
        <article
          key={c.title}
          className="bg-panel border border-default rounded-lg p-4"
        >
          <div className="flex items-baseline justify-between gap-3">
            <h4 className="text-13 font-medium leading-heading text-primary">
              {c.title}
            </h4>
            <span className="text-11 font-mono uppercase tracking-mono-caps text-tertiary whitespace-nowrap">
              {c.meta}
            </span>
          </div>
          <p className="mt-3 text-13 leading-prose text-secondary">
            {c.snippet}
          </p>
        </article>
      ))}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   5b. Pricing
   ────────────────────────────────────────────────────────────── */

function Pricing({ signedIn }: { signedIn: boolean }) {
  return (
    <section id="pricing" className="scroll-mt-16 border-b border-default">
      <Container className="py-16 md:py-24">
        <SectionEyebrow>Pricing</SectionEyebrow>
        <SectionHeading className="mt-4 max-w-2xl">
          Start free. Then pay only for the applications you send.
        </SectionHeading>
        <p className="mt-5 text-15 leading-prose text-secondary max-w-2xl">
          Scoring how well you fit a role is always free, as many as you like. A
          Warp, the full application JobWarp generates for a posting (cover
          letter, screening answers, company research), is the only thing that
          costs a credit. No subscription, and credits never expire.
        </p>

        <div className="mt-8 flex flex-col gap-5 rounded-xl border border-accent-lime bg-panel p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <SectionEyebrow>Free to start</SectionEyebrow>
            <h3 className="mt-2 text-20 font-medium leading-heading tracking-heading text-primary">
              Sign up and get 3 Warps free.
            </h3>
            <p className="mt-1.5 text-13 leading-prose text-secondary max-w-md">
              Score as many roles as you like for nothing, then put your first
              three full applications to work. No card, no trial countdown, and
              nothing expires.
            </p>
          </div>
          <Link
            to={signedIn ? "/app" : "/signin?mode=signup"}
            className="btn-primary flex-none hover:shadow-focus"
          >
            {signedIn ? "Open JobWarp" : "Sign up free"}
          </Link>
        </div>

        <p className="mt-12 text-11 font-mono uppercase tracking-mono-caps text-tertiary">
          When you need more
        </p>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {CREDIT_PACKS.map((pack) => (
            <PackCard key={pack.id} pack={pack} signedIn={signedIn} />
          ))}
        </div>
      </Container>
    </section>
  );
}

function PackCard({
  pack,
  signedIn,
}: {
  pack: (typeof CREDIT_PACKS)[number];
  signedIn: boolean;
}) {
  const perWarp = (pack.priceUsd / pack.warps).toFixed(2);
  const href = signedIn ? "/app/credits" : "/signin?mode=signup";
  return (
    <div
      className={`relative flex flex-col rounded-xl border bg-panel p-5 transition-all duration-300 ${
        pack.popular
          ? "border-accent-lime hover:shadow-focus"
          : "border-default hover:border-strong"
      }`}
    >
      {pack.popular && (
        <span className="absolute right-3 top-3 inline-flex items-center rounded-full border border-accent-lime-30 bg-accent-lime-06 px-2 py-0.5 font-mono text-[10px] uppercase tracking-mono-caps text-accent-lime">
          Most popular
        </span>
      )}
      <div className="text-11 font-mono uppercase tracking-mono-caps text-tertiary">
        {pack.label}
      </div>
      <div className="mt-2 flex items-baseline gap-1.5">
        <span className="text-28 font-medium leading-none text-primary">{pack.warps}</span>
        <span className="text-13 text-tertiary">Warps</span>
      </div>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="text-20 font-medium text-primary">${pack.priceUsd}</span>
        <span className="text-11 text-tertiary">${perWarp} per Warp</span>
      </div>
      <Link
        to={href}
        className={`${pack.popular ? "btn-primary" : "btn-secondary"} mt-5 w-full justify-center`}
      >
        {signedIn ? `Buy ${pack.label}` : "Get started"}
      </Link>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   6. FAQ
   ────────────────────────────────────────────────────────────── */

const FAQ_ITEMS = [
  {
    q: "What does JobWarp cost?",
    a: "JobWarp is pay as you go, with no subscription. Scoring how well you fit a role is always free. You spend one credit only when you Warp a posting, that is, when JobWarp generates the full application for it. Your first 3 Warps are free. After that, packs start at $9 for 10 Warps, and credits never expire.",
  },
  {
    q: "Which job sites does it work on?",
    a: "Any website with a job description. LinkedIn, Indeed, Greenhouse, and Workday are auto-detected. On other job boards, ATS portals, and company career pages, JobWarp tries to read the JD straight from the page. If it cannot find it automatically, you click the JD block on the page and JobWarp reads it from there.",
  },
  {
    q: "How accurate are the fit scores?",
    a: "They are guidance, not gospel. Treat them like a friend's opinion. Strong matches are reliable. Borderline scores between five and seven are where your judgment matters most. We tune to be a little pessimistic rather than flatter you.",
  },
  {
    q: "What data does JobWarp collect?",
    a: "Your CV, the JDs you choose to score, and the applications you choose to log. The extension reads only the page you are on, and only when you ask it to. No background scraping. No tracking pixels on other tabs.",
  },
  {
    q: "Where does my CV go?",
    a: "To our backend on Cloudflare and to the LLM that generates the drafts. We do not sell, share, or fine-tune anything on your data. You can delete your profile and all generated content at any time.",
  },
  {
    q: "Will my employer see this?",
    a: "No. JobWarp runs locally in your browser. No emails to companies on your behalf. No public profile. The browser toolbar icon is the only visible footprint, and you can pin or unpin it.",
  },
  {
    q: "Can I edit the generated cover letter?",
    a: "Yes. Every generated piece opens in a normal editor. Edit, regenerate sections you do not like, or rewrite the whole thing. The draft is a starting point, not the final word.",
  },
  {
    q: "How is this different from Teal or Jobscan?",
    a: "Those tools help you after you have decided to apply. JobWarp helps you decide. The qualification happens on the JD page in one click, before you invest the thirty minutes. The funnel tracking and material generation are also tighter and require less manual upkeep.",
  },
];

function FAQ() {
  return (
    <section id="faq" className="scroll-mt-16 border-b border-default">
      <Container className="py-16 md:py-24">
        <div className="grid lg:grid-cols-[280px_1fr] gap-12 lg:gap-16">
          <div>
            <SectionEyebrow>Questions before you start</SectionEyebrow>
            <SectionHeading className="mt-4">FAQ</SectionHeading>
          </div>
          <div className="bg-panel border border-default rounded-xl overflow-hidden">
            {FAQ_ITEMS.map((item, i) => (
              <FAQRow key={item.q} q={item.q} a={item.a} first={i === 0} />
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}

function FAQRow({ q, a, first }: { q: string; a: string; first: boolean }) {
  return (
    <details className={`group ${first ? "" : "border-t border-default"}`}>
      <summary className="flex items-start justify-between gap-4 cursor-pointer list-none px-5 py-5 hover:bg-elevated transition-colors">
        <span className="text-15 font-medium leading-heading text-primary">
          {q}
        </span>
        <IconChevronDown
          size={16}
          stroke={1.5}
          className="mt-1 shrink-0 text-tertiary transition-transform group-open:rotate-180"
        />
      </summary>
      <div className="px-5 pb-5 -mt-1">
        <p className="text-13 leading-prose text-secondary max-w-2xl">{a}</p>
      </div>
    </details>
  );
}

/* ──────────────────────────────────────────────────────────────
   7. Final CTA
   ────────────────────────────────────────────────────────────── */

function FinalCTA({ signedIn }: { signedIn: boolean }) {
  return (
    <section className="border-b border-default">
      <Container className="py-16 md:py-24">
        <div className="bg-panel border border-default rounded-xl p-8 md:p-12 relative overflow-hidden">
          {/* Decorative dust mark on the right at low contrast, dust-as-texture. */}
          <DustMark
            className="hidden md:block absolute -right-12 -bottom-12 w-80 h-auto text-elevated"
            aria-hidden="true"
          />
          <div className="relative">
            <SectionEyebrow>Stop sending generic applications</SectionEyebrow>
            <h2 className="mt-4 text-28 font-medium leading-display tracking-heading text-primary max-w-2xl">
              More interviews. Less time per application.
            </h2>
            <p className="mt-5 text-15 leading-prose text-secondary max-w-xl">
              Sign up free and score your next posting in under a minute.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <SignUpButton signedIn={signedIn} />
            </div>
            <p className="mt-5 text-11 font-mono uppercase tracking-mono-caps text-tertiary">
              Free to start, no card. Works in Chrome, Edge &amp; Firefox.
            </p>
          </div>
        </div>
      </Container>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────
   Footer
   ────────────────────────────────────────────────────────────── */

function SiteFooter() {
  return (
    <footer>
      <Container className="py-8 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <DustMark className="w-5 h-5 text-tertiary" />
          <Wordmark size={15} />
        </div>
        <div className="flex items-center gap-6 text-11 font-mono uppercase tracking-mono-caps text-tertiary">
          <Link to="/privacy/" className="hover:text-primary">Privacy</Link>
          <Link to="/terms/" className="hover:text-primary">Terms</Link>
          <span>© 2026 JobWarp</span>
        </div>
      </Container>
    </footer>
  );
}

/* ──────────────────────────────────────────────────────────────
   Dust mark — inline SVG so it inherits currentColor
   ────────────────────────────────────────────────────────────── */

function DustMark({
  className = "",
  ...props
}: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 59 57"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <path d="M25.0859 55.6719C25.3621 55.6719 25.5859 55.8957 25.5859 56.1719C25.5859 56.448 25.3621 56.6719 25.0859 56.6719C24.8098 56.6719 24.5859 56.448 24.5859 56.1719C24.5859 55.8957 24.8098 55.6719 25.0859 55.6719ZM30.5859 53.6719C31.1382 53.6719 31.5859 54.1196 31.5859 54.6719C31.5859 55.2242 31.1382 55.6719 30.5859 55.6719C30.0337 55.6719 29.5859 55.2242 29.5859 54.6719C29.5859 54.1196 30.0337 53.6719 30.5859 53.6719ZM35.0859 50.1719C35.6382 50.1719 36.0859 50.6196 36.0859 51.1719C36.0859 51.7242 35.6382 52.1719 35.0859 52.1719C34.5337 52.1719 34.0859 51.7242 34.0859 51.1719C34.0859 50.6196 34.5337 50.1719 35.0859 50.1719ZM15.8359 46.1719C16.5263 46.1719 17.0859 46.7315 17.0859 47.4219C17.0859 48.1122 16.5263 48.6719 15.8359 48.6719C15.1456 48.6719 14.5859 48.1122 14.5859 47.4219C14.5859 46.7315 15.1456 46.1719 15.8359 46.1719ZM10.2197 45.6719C10.772 45.6719 11.2197 46.1196 11.2197 46.6719C11.2197 47.2242 10.772 47.6719 10.2197 47.6719C9.66744 47.6719 9.21973 47.2242 9.21973 46.6719C9.21973 46.1196 9.66744 45.6719 10.2197 45.6719ZM22.3359 44.1719C23.3024 44.1719 24.0859 44.9554 24.0859 45.9219C24.0859 46.8884 23.3024 47.6719 22.3359 47.6719C21.3694 47.6719 20.5859 46.8884 20.5859 45.9219C20.5859 44.9554 21.3694 44.1719 22.3359 44.1719ZM38.8359 44.1719C39.8024 44.1719 40.5859 44.9554 40.5859 45.9219C40.5859 46.8884 39.8024 47.6719 38.8359 47.6719C37.8694 47.6719 37.0859 46.8884 37.0859 45.9219C37.0859 44.9554 37.8694 44.1719 38.8359 44.1719ZM51.0732 44.7842C51.3494 44.7842 51.5732 45.008 51.5732 45.2842C51.573 45.5601 51.3492 45.7842 51.0732 45.7842C50.7973 45.7841 50.5735 45.5601 50.5732 45.2842C50.5732 45.0081 50.7972 44.7843 51.0732 44.7842ZM5.7959 43.7646C6.07204 43.7646 6.2959 43.9885 6.2959 44.2646C6.29582 44.5407 6.072 44.7646 5.7959 44.7646C5.51982 44.7646 5.29597 44.5407 5.2959 44.2646C5.2959 43.9885 5.51978 43.7647 5.7959 43.7646ZM28.542 40.5322C29.3703 40.5322 30.0417 41.204 30.042 42.0322C30.042 42.8607 29.3704 43.5322 28.542 43.5322C27.7137 43.5321 27.042 42.8606 27.042 42.0322C27.0422 41.2041 27.7138 40.5324 28.542 40.5322ZM52.3359 41.6719C52.7502 41.6719 53.0859 42.0077 53.0859 42.4219C53.0859 42.8361 52.7502 43.1719 52.3359 43.1719C51.9217 43.1719 51.5859 42.8361 51.5859 42.4219C51.5859 42.0077 51.9217 41.6719 52.3359 41.6719ZM41.5859 38.1719C42.4144 38.1719 43.0859 38.8434 43.0859 39.6719C43.0859 40.5003 42.4144 41.1719 41.5859 41.1719C40.7575 41.1719 40.0859 40.5003 40.0859 39.6719C40.0859 38.8434 40.7575 38.1719 41.5859 38.1719ZM31.7451 37.1123C32.2974 37.1123 32.7451 37.5601 32.7451 38.1123C32.7451 38.6646 32.2974 39.1123 31.7451 39.1123C31.193 39.1122 30.7451 38.6645 30.7451 38.1123C30.7452 37.5601 31.193 37.1124 31.7451 37.1123ZM15.5195 35.5381C16.3847 35.5383 17.0859 36.2403 17.0859 37.1055C17.0857 37.9705 16.3845 38.6716 15.5195 38.6719C14.6543 38.6719 13.9524 37.9706 13.9521 37.1055C13.9521 36.2401 14.6542 35.5381 15.5195 35.5381ZM53.5859 36.6719C54.1382 36.6719 54.5859 37.1196 54.5859 37.6719C54.5859 38.2242 54.1382 38.6719 53.5859 38.6719C53.0337 38.6719 52.5859 38.2242 52.5859 37.6719C52.5859 37.1196 53.0337 36.6719 53.5859 36.6719ZM21.5859 35.6719C22.1382 35.6719 22.5859 36.1196 22.5859 36.6719C22.5859 37.2242 22.1382 37.6719 21.5859 37.6719C21.0337 37.6719 20.5859 37.2242 20.5859 36.6719C20.5859 36.1196 21.0337 35.6719 21.5859 35.6719ZM9.65234 33.1719C10.5176 33.1719 11.2195 33.8731 11.2197 34.7383C11.2197 35.6037 10.5177 36.3057 9.65234 36.3057C8.7872 36.3054 8.08594 35.6035 8.08594 34.7383C8.0862 33.8733 8.78736 33.1721 9.65234 33.1719ZM41.2734 32C42.378 32 43.2734 32.8954 43.2734 34C43.2734 35.1045 42.378 36 41.2734 36C40.1689 36 39.2735 35.1045 39.2734 34C39.2734 32.8954 40.1689 32 41.2734 32ZM25.2529 34.6436C25.529 34.6436 25.7529 34.8675 25.7529 35.1436C25.7528 35.4196 25.5289 35.6435 25.2529 35.6436C24.9769 35.6436 24.753 35.4196 24.7529 35.1436C24.7529 34.8674 24.9768 34.6436 25.2529 34.6436ZM33.0859 33.6719C33.3621 33.6719 33.5859 33.8957 33.5859 34.1719C33.5859 34.448 33.3621 34.6719 33.0859 34.6719C32.8098 34.6719 32.5859 34.448 32.5859 34.1719C32.5859 33.8957 32.8098 33.6719 33.0859 33.6719ZM6.58594 31.6719C7.13822 31.6719 7.58594 32.1196 7.58594 32.6719C7.58594 33.2242 7.13822 33.6719 6.58594 33.6719C6.03365 33.6719 5.58594 33.2242 5.58594 32.6719C5.58594 32.1196 6.03365 31.6719 6.58594 31.6719ZM53.0859 30.6719C53.9144 30.6719 54.5859 31.3434 54.5859 32.1719C54.5859 33.0003 53.9144 33.6719 53.0859 33.6719C52.2575 33.6719 51.5859 33.0003 51.5859 32.1719C51.5859 31.3434 52.2575 30.6719 53.0859 30.6719ZM21.9766 29.9834C22.2526 29.9834 22.4763 30.2075 22.4766 30.4834C22.4766 30.7595 22.2527 30.9834 21.9766 30.9834C21.7005 30.9833 21.4766 30.7595 21.4766 30.4834C21.4768 30.2075 21.7006 29.9835 21.9766 29.9834ZM19.1377 29.4512C19.4137 29.4514 19.6377 29.6751 19.6377 29.9512C19.6375 30.227 19.4136 30.451 19.1377 30.4512C18.8617 30.4512 18.6379 30.2272 18.6377 29.9512C18.6377 29.675 18.8616 29.4512 19.1377 29.4512ZM39.1113 28.7275C39.2186 28.3275 39.6302 28.0901 40.0303 28.1973C40.4302 28.3046 40.6677 28.7162 40.5605 29.1162C40.4532 29.5161 40.0416 29.7537 39.6416 29.6465C39.2415 29.5393 39.0041 29.1276 39.1113 28.7275ZM3.04004 27.4717C3.59232 27.4717 4.04004 27.9194 4.04004 28.4717C4.03993 29.0239 3.59225 29.4717 3.04004 29.4717C2.48794 29.4715 2.04015 29.0238 2.04004 28.4717C2.04004 27.9195 2.48787 27.4718 3.04004 27.4717ZM15.5859 26.1719C16.4144 26.1719 17.0859 26.8434 17.0859 27.6719C17.0859 28.5003 16.4144 29.1719 15.5859 29.1719C14.7575 29.1719 14.0859 28.5003 14.0859 27.6719C14.0859 26.8434 14.7575 26.1719 15.5859 26.1719ZM29.0859 21.1719C31.2951 21.1719 33.0859 22.9627 33.0859 25.1719C33.0859 27.381 31.2951 29.1719 29.0859 29.1719C26.8768 29.1719 25.0859 27.381 25.0859 25.1719C25.0859 22.9627 26.8768 21.1719 29.0859 21.1719ZM50.5859 25.1719C51.4144 25.1719 52.0859 25.8434 52.0859 26.6719C52.0859 27.5003 51.4144 28.1719 50.5859 28.1719C49.7575 28.1719 49.0859 27.5003 49.0859 26.6719C49.0859 25.8434 49.7575 25.1719 50.5859 25.1719ZM38.1455 25.4502C38.4826 25.4504 38.7559 25.7244 38.7559 26.0615C38.7557 26.3985 38.4825 26.6716 38.1455 26.6719C37.8084 26.6719 37.5343 26.3986 37.5342 26.0615C37.5342 25.7243 37.8083 25.4502 38.1455 25.4502ZM1.08594 23.6719C1.36208 23.6719 1.58594 23.8957 1.58594 24.1719C1.58594 24.448 1.36208 24.6719 1.08594 24.6719C0.809795 24.6719 0.585938 24.448 0.585938 24.1719C0.585938 23.8957 0.809795 23.6719 1.08594 23.6719ZM12.0859 21.5381C12.9513 21.5381 13.6523 22.2401 13.6523 23.1055C13.6521 23.9706 12.9511 24.6719 12.0859 24.6719C11.2207 24.6719 10.5198 23.9706 10.5195 23.1055C10.5195 22.2401 11.2206 21.5381 12.0859 21.5381ZM21.1416 23.4424C21.4175 23.4425 21.6414 23.6665 21.6416 23.9424C21.6416 24.2185 21.4176 24.4423 21.1416 24.4424C20.8655 24.4424 20.6416 24.2185 20.6416 23.9424C20.6418 23.6664 20.8656 23.4424 21.1416 23.4424ZM46.0859 20.6719C46.9144 20.6719 47.5859 21.3434 47.5859 22.1719C47.5859 23.0003 46.9144 23.6719 46.0859 23.6719C45.2575 23.6719 44.5859 23.0003 44.5859 22.1719C44.5859 21.3434 45.2575 20.6719 46.0859 20.6719ZM20.1426 21.4404C20.4186 21.4405 20.6424 21.6644 20.6426 21.9404C20.6426 22.2165 20.4187 22.4404 20.1426 22.4404C19.8664 22.4404 19.6426 22.2166 19.6426 21.9404C19.6427 21.6644 19.8665 21.4404 20.1426 21.4404ZM0.5 19.8926C0.776142 19.8926 1 20.1164 1 20.3926C0.999764 20.6685 0.775997 20.8926 0.5 20.8926C0.224198 20.8923 0.000235557 20.6684 0 20.3926C0 20.1166 0.224053 19.8928 0.5 19.8926ZM57.5859 19.6719C57.8621 19.6719 58.0859 19.8957 58.0859 20.1719C58.0859 20.448 57.8621 20.6719 57.5859 20.6719C57.3098 20.6719 57.0859 20.448 57.0859 20.1719C57.0859 19.8957 57.3098 19.6719 57.5859 19.6719ZM40.8916 18.1904C41.4438 18.1904 41.8915 18.6383 41.8916 19.1904C41.8916 19.7427 41.4439 20.1904 40.8916 20.1904C40.3394 20.1903 39.8916 19.7426 39.8916 19.1904C39.8917 18.6384 40.3395 18.1906 40.8916 18.1904ZM19.8359 18.1719C20.2502 18.1719 20.5859 18.5077 20.5859 18.9219C20.5859 19.3361 20.2502 19.6719 19.8359 19.6719C19.4217 19.6719 19.0859 19.3361 19.0859 18.9219C19.0859 18.5077 19.4217 18.1719 19.8359 18.1719ZM35.5859 18.1719C35.8621 18.1719 36.0859 18.3957 36.0859 18.6719C36.0859 18.948 35.8621 19.1719 35.5859 19.1719C35.3098 19.1719 35.0859 18.948 35.0859 18.6719C35.0859 18.3957 35.3098 18.1719 35.5859 18.1719ZM10.5859 15.1719C11.4144 15.1719 12.0859 15.8434 12.0859 16.6719C12.0859 17.5003 11.4144 18.1719 10.5859 18.1719C9.75751 18.1719 9.08594 17.5003 9.08594 16.6719C9.08594 15.8434 9.75751 15.1719 10.5859 15.1719ZM27.5859 17.1719C27.8621 17.1719 28.0859 17.3957 28.0859 17.6719C28.0859 17.948 27.8621 18.1719 27.5859 18.1719C27.3098 18.1719 27.0859 17.948 27.0859 17.6719C27.0859 17.3957 27.3098 17.1719 27.5859 17.1719ZM55.0859 16.1719C55.6382 16.1719 56.0859 16.6196 56.0859 17.1719C56.0859 17.7242 55.6382 18.1719 55.0859 18.1719C54.5337 18.1719 54.0859 17.7242 54.0859 17.1719C54.0859 16.6196 54.5337 16.1719 55.0859 16.1719ZM28.5859 15.1719C28.8621 15.1719 29.0859 15.3957 29.0859 15.6719C29.0859 15.948 28.8621 16.1719 28.5859 16.1719C28.3098 16.1719 28.0859 15.948 28.0859 15.6719C28.0859 15.3957 28.3098 15.1719 28.5859 15.1719ZM20.3359 13.1719C21.0263 13.1719 21.5859 13.7315 21.5859 14.4219C21.5859 15.1122 21.0263 15.6719 20.3359 15.6719C19.6456 15.6719 19.0859 15.1122 19.0859 14.4219C19.0859 13.7315 19.6456 13.1719 20.3359 13.1719ZM31.3359 12.1719C32.0263 12.1719 32.5859 12.7315 32.5859 13.4219C32.5859 14.1122 32.0263 14.6719 31.3359 14.6719C30.6456 14.6719 30.0859 14.1122 30.0859 13.4219C30.0859 12.7315 30.6456 12.1719 31.3359 12.1719ZM51.3359 12.6719C51.8882 12.6719 52.3359 13.1196 52.3359 13.6719C52.3359 14.2242 51.8882 14.6719 51.3359 14.6719C50.7837 14.6719 50.3359 14.2242 50.3359 13.6719C50.3359 13.1196 50.7837 12.6719 51.3359 12.6719ZM35.5859 10.1719C36.4144 10.1719 37.0859 10.8434 37.0859 11.6719C37.0859 12.5003 36.4144 13.1719 35.5859 13.1719C34.7575 13.1719 34.0859 12.5003 34.0859 11.6719C34.0859 10.8434 34.7575 10.1719 35.5859 10.1719ZM42.0859 9.17188C43.1905 9.17188 44.0859 10.0673 44.0859 11.1719C44.0859 12.2764 43.1905 13.1719 42.0859 13.1719C40.9814 13.1719 40.0859 12.2764 40.0859 11.1719C40.0859 10.0673 40.9814 9.17188 42.0859 9.17188ZM47.0859 11.1719C47.6382 11.1719 48.0859 11.6196 48.0859 12.1719C48.0859 12.7242 47.6382 13.1719 47.0859 13.1719C46.5337 13.1719 46.0859 12.7242 46.0859 12.1719C46.0859 11.6196 46.5337 11.1719 47.0859 11.1719ZM11.5859 9.30566C12.1382 9.30566 12.5859 9.75338 12.5859 10.3057C12.5859 10.8579 12.1382 11.3057 11.5859 11.3057C11.0337 11.3057 10.5859 10.8579 10.5859 10.3057C10.5859 9.75338 11.0337 9.30566 11.5859 9.30566ZM23.293 7.08691C24.0068 7.08691 24.5858 7.66512 24.5859 8.37891C24.5859 9.09283 24.0069 9.67188 23.293 9.67188C22.5792 9.67172 22.001 9.09273 22.001 8.37891C22.0011 7.66521 22.5793 7.08707 23.293 7.08691ZM13.8359 4.17188C14.2502 4.17188 14.5859 4.50766 14.5859 4.92188C14.5859 5.33609 14.2502 5.67188 13.8359 5.67188C13.4217 5.67187 13.0859 5.33609 13.0859 4.92188C13.0859 4.50766 13.4217 4.17188 13.8359 4.17188ZM28.0859 2.67188C28.7763 2.67188 29.3359 3.23152 29.3359 3.92188C29.3359 4.61223 28.7763 5.17188 28.0859 5.17188C27.3956 5.17188 26.8359 4.61223 26.8359 3.92188C26.8359 3.23152 27.3956 2.67188 28.0859 2.67188ZM33.0859 0.671875C33.6382 0.671875 34.0859 1.11959 34.0859 1.67188C34.0859 2.22416 33.6382 2.67188 33.0859 2.67188C32.5337 2.67188 32.0859 2.22416 32.0859 1.67188C32.0859 1.11959 32.5337 0.671875 33.0859 0.671875ZM16.5859 1.17188C16.8621 1.17188 17.0859 1.39573 17.0859 1.67188C17.0859 1.94802 16.8621 2.17188 16.5859 2.17188C16.3098 2.17188 16.0859 1.94802 16.0859 1.67188C16.0859 1.39573 16.3098 1.17188 16.5859 1.17188ZM37.2158 0C37.63 5.35919e-05 37.9658 0.33584 37.9658 0.75C37.9658 1.16418 37.63 1.49995 37.2158 1.5C36.8016 1.5 36.4658 1.16421 36.4658 0.75C36.4658 0.335806 36.8016 0 37.2158 0ZM41.5859 0.171875C41.8621 0.171875 42.0859 0.395733 42.0859 0.671875C42.0859 0.948017 41.8621 1.17188 41.5859 1.17188C41.3098 1.17188 41.0859 0.948017 41.0859 0.671875C41.0859 0.395733 41.3098 0.171875 41.5859 0.171875Z" />
    </svg>
  );
}
